import { useCallback, useEffect, useRef, useState } from 'react';
import { Package, PackageSearch, X } from 'lucide-react';
import { Card, CardContent, EmptyState, ErrorState } from '@mustauto/ui/web';
import { api } from '@/lib/api';
import { formatStockQuantity } from '@mustauto/utils';
import { formatCentavos } from '@/lib/utils';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { useFocusTrap } from '@/hooks/useFocusTrap';

/**
 * Busca de peça no estoque da oficina, para montar a OS pelo painel.
 *
 * O painel não tinha isto: "Adicionar item" criava uma linha em branco e o
 * gestor digitava o nome da peça à mão. Além do retrabalho, o item digitado
 * nasce sem `inventoryItemId` e sem `origin: 'stock'` — e é exatamente esse
 * par que `deductInventoryForOS` exige para debitar. Ou seja, a peça saía da
 * prateleira, entrava na OS, e o estoque nunca baixava. Silenciosamente.
 *
 * O comportamento espelha o `AddPartModal` do app de propósito: é o mesmo
 * endpoint (`/parts/search`), a mesma regra de origem e o mesmo teto de
 * quantidade. Gestor e mecânico trabalham na mesma oficina e não deveriam
 * aprender duas regras para a mesma peça.
 */

export interface StockSearchResult {
  id: string;
  name: string;
  sku: string | null;
  category: string;
  /** Em CENTAVOS, como `/parts/search` devolve. */
  salePrice: number;
  quantity: number;
  minQuantity: number;
  /** Unidade do estoque ("litro", "ml"…). Ausente numa API anterior à 0110. */
  unit?: string | null;
}

/** O que o seletor entrega: já no formato do item da OS, em REAIS, porque é
 *  assim que o formulário trabalha (a conversão para centavos acontece no
 *  envio, junto com a dos outros itens). */
export interface PickedStockPart {
  description: string;
  code: string | null;
  unitPrice: number;
  origin: 'stock' | 'supplier';
  inventoryItemId: string;
  /** Saldo em mãos no momento da escolha — o formulário usa como teto da
   *  quantidade. Não vai para a API. */
  available: number;
}

interface StockPartPickerProps {
  onClose: () => void;
  onPick: (part: PickedStockPart) => void;
  /** Filtro de compatibilidade, como no app. */
  vehicleMake?: string;
  vehicleModel?: string;
}

/** Montado pelo chamador com `{aberto && <StockPartPicker …/>}`, como os
 *  outros modais do painel. Esconder-se com `if (!open) return null` mantendo
 *  o componente montado parecia equivalente e não é: o `useFocusTrap` arma uma
 *  vez, na montagem, e encontrava `ref.current` nulo — o Tab escapava do
 *  diálogo para o formulário atrás do overlay, e o foco não voltava ao fechar. */
export function StockPartPicker({
  onClose,
  onPick,
  vehicleMake,
  vehicleModel,
}: StockPartPickerProps) {
  useEscapeKey(onClose);
  const trapRef = useFocusTrap<HTMLDivElement>();

  const [query, setQuery] = useState('');
  const [results, setResults] = useState<StockSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  // Falha de busca é diferente de "não tem essa peça": sem a distinção, uma
  // queda de rede diria ao gestor que a peça não existe no estoque dele.
  const [error, setError] = useState<unknown>(null);
  const [attempt, setAttempt] = useState(0);

  const trimmed = query.trim();

  useEffect(() => {
    if (trimmed.length < 2) {
      setResults([]);
      setLoading(false);
      setError(null);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);

    const timer = setTimeout(() => {
      api
        .get('/parts/search', {
          params: { q: trimmed, vehicleMake, vehicleModel },
        })
        .then((res) => {
          if (!cancelled) setResults(res.data.results ?? []);
        })
        .catch((err) => {
          if (!cancelled) {
            setResults([]);
            setError(err);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 300);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [trimmed, vehicleMake, vehicleModel, attempt]);

  // Foco no campo ao abrir — quem abriu já sabe o que vai procurar.
  const inputRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  const handlePick = useCallback(
    (item: StockSearchResult) => {
      const zeroed = item.quantity <= 0;
      onPick({
        description: item.name,
        code: item.sku,
        // `/parts/search` responde em centavos; o formulário é em reais.
        unitPrice: item.salePrice / 100,
        // Item zerado não pode sair da prateleira — vira compra de
        // fornecedor, como o app já faz. Marcá-lo como `stock` prometeria
        // uma baixa que não vai acontecer.
        origin: zeroed ? 'supplier' : 'stock',
        inventoryItemId: item.id,
        available: item.quantity,
      });
      setQuery('');
      setResults([]);
      onClose();
    },
    [onPick, onClose],
  );

  return (
    <div
      ref={trapRef}
      role="dialog"
      aria-modal="true"
      aria-label="Buscar peça no estoque"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 pt-20"
    >
      <Card className="w-full max-w-lg max-h-[70vh] overflow-y-auto">
        <CardContent className="pt-6">
          <div className="mb-4 flex items-center justify-between">
            <h3 className="text-base font-semibold">Buscar peça no estoque</h3>
            <button
              onClick={onClose}
              aria-label="Fechar"
              className="rounded-md p-1 hover:bg-muted"
            >
              <X size={18} />
            </button>
          </div>

          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Nome ou código da peça"
            aria-label="Nome ou código da peça"
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
          />

          <div className="mt-4">
            {trimmed.length < 2 ? (
              <p className="py-8 text-center text-xs text-muted-foreground">
                Digite ao menos 2 letras para buscar.
              </p>
            ) : loading ? (
              <p className="py-8 text-center text-xs text-muted-foreground">Buscando…</p>
            ) : error ? (
              <ErrorState
                title="Não foi possível buscar no estoque"
                error={error}
                onRetry={() => setAttempt((n) => n + 1)}
              />
            ) : results.length === 0 ? (
              <EmptyState
                compact
                icon={<PackageSearch size={22} />}
                title="Nenhuma peça encontrada"
                description="Confira o nome ou cadastre a peça em Estoque. Você também pode fechar e digitar o item à mão."
              />
            ) : (
              <ul className="divide-y">
                {results.map((item) => {
                  const zeroed = item.quantity <= 0;
                  const low = !zeroed && item.quantity <= item.minQuantity;
                  return (
                    <li key={item.id}>
                      <button
                        type="button"
                        onClick={() => handlePick(item)}
                        className="flex w-full items-center gap-3 py-3 text-left hover:bg-muted/50"
                      >
                        <Package size={16} className="shrink-0 text-muted-foreground" />
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-sm font-medium">{item.name}</span>
                          <span className="block text-xs text-muted-foreground">
                            {item.sku ? `${item.sku} · ` : ''}
                            {zeroed
                              ? 'sem saldo — entra como compra'
                              : `${formatStockQuantity(item.quantity, item.unit)} em estoque`}
                            {low ? ' (abaixo do mínimo)' : ''}
                          </span>
                        </span>
                        <span className="shrink-0 text-sm font-semibold">
                          {formatCentavos(item.salePrice)}
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
